import { Text, Title } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { useGetShiftBoosts } from "@src/appV2/redesign/Shift/ShiftBoost/api/useGetShiftBoosts";
import { type Bookings } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { generatePath } from "react-router-dom";

import { useIsInHomeEnabled } from "../../InHome/lib/useIsInHomeEnabled";
import { getFormattedShiftPayBreakdown } from "../../Shift/getFormattedShiftPayBreakdown";
import { WorkerShiftCard } from "../../Shift/WorkerShift/Card";
import { type WorkersForOverlappingShiftSlots } from "../../WorkWithFriends/hooks/useGetWorkersForOverlappingShiftSlots";
import { SHIFT_DETAILS_FULL_PATH } from "../path";
import { WorkerInterviewCard } from "./WorkerInterviewCard";

interface ScheduledBookingsProps {
  bookings: Bookings[];
  workersForOverlappingShiftSlotByShiftId: Record<string, WorkersForOverlappingShiftSlots>;
}

export function ScheduledBookings(props: ScheduledBookingsProps) {
  const { bookings, workersForOverlappingShiftSlotByShiftId } = props;

  const isInHomeRedesignEnabled = useIsInHomeEnabled();
  const worker = useDefinedWorker();
  const shiftBoostConfig = useCbhFlag(CbhFeatureFlag.SHIFT_BOOST_CONFIG, {
    defaultValue: { enabled: false },
  });
  const { data: shiftBoosts } = useGetShiftBoosts(
    {
      workerId: worker.userId,
      statuses: ["APPLIED"],
    },
    { enabled: shiftBoostConfig.enabled }
  );

  const allQualificationNames = bookings
    .filter((booking) => booking.type === "shift")
    .map((booking) => booking.data.agentReq);

  const { qualificationNameToDisplayLabelMap } =
    useGetQualificationDisplayLabels(allQualificationNames);

  return (
    <Stack spacing={4}>
      {bookings.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={4} sx={{ paddingX: 2 }}>
          <Title component="h2" variant="h5" paddingY={3}>
            {isInHomeRedesignEnabled ? "Other scheduled" : "Scheduled"}{" "}
          </Title>
          <Text semibold variant="body2" color={(theme) => theme.palette.text.tertiary}>
            {bookings.length}
          </Text>
        </Stack>
      )}

      {bookings.map((booking) => {
        if (booking.type === "shift") {
          const shift = booking.data;

          const appliedShiftBoost = shiftBoosts?.data.find(
            (boost) => boost.relationships.shift?.data.id === shift._id
          );
          const payBreakdown = getFormattedShiftPayBreakdown(
            shift.offer?.pay.value,
            shift.finalPay,
            shift.originalAmount,
            appliedShiftBoost?.attributes.amount
          );
          return (
            <WorkerShiftCard
              key={shift._id}
              href={generatePath(SHIFT_DETAILS_FULL_PATH, {
                shiftId: shift._id,
              })}
              workplaceId={shift.facility.userId ?? ""}
              workplaceName={shift.facility.name}
              workplaceTimezone={shift.facility.tmz ?? ""}
              workplaceRequiresLunchBreak={shift.facility.requiresLunchBreak ?? false}
              shiftTimeSlot={shiftPeriodToTimeSlot[shift.name]}
              shiftStart={shift.start}
              shiftEnd={shift.end}
              shiftQualificationName={
                qualificationNameToDisplayLabelMap.get(shift.agentReq) ?? shift.agentReq
              }
              shiftHourlyPay={payBreakdown?.hourlyPay}
              shiftTotalPay={payBreakdown?.totalPay}
              shiftTotalWithBoostPay={payBreakdown?.totalWithBoostPay}
              workersForOverlappingShiftSlot={workersForOverlappingShiftSlotByShiftId[shift._id]}
            />
          );
        }

        if (booking.type === "interview") {
          return (
            <WorkerInterviewCard
              key={booking.data.id}
              interviewId={booking.data.id}
              start={booking.data.start}
              end={booking.data.end}
              status={booking.data.status}
              placementId={booking.data.placementId}
              workerTypes={booking.data.placement?.workerTypes ?? []}
              workplaceName={booking.data.workplace?.name ?? ""}
              workplaceDistance={booking.data.workplace?.distance}
            />
          );
        }

        return null;
      })}
    </Stack>
  );
}
